import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, User, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const ActivityLogs = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api.get('/activity-logs?limit=200');
      setLogs(response.data);
    } catch (error) {
      toast.error('Gagal memuat log aktivitas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getActionBadge = (action) => {
    const styles = {
      CREATE: 'bg-green-100 text-green-700',
      UPDATE: 'bg-blue-100 text-blue-700',
      DELETE: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[action] || 'bg-slate-100 text-slate-800'}>{action}</Badge>;
  };

  const getEntityBadge = (entityType) => {
    const styles = {
      PURCHASE: 'bg-orange-100 text-orange-700',
      EXPENSE: 'bg-purple-100 text-purple-700',
      SALE: 'bg-teal-100 text-teal-700',
      BRANCH: 'bg-indigo-100 text-indigo-700',
      MANAGER: 'bg-pink-100 text-pink-700',
      INVESTOR: 'bg-amber-100 text-amber-700'
    };
    return <Badge className={styles[entityType] || 'bg-slate-100 text-slate-800'}>{entityType}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>
          Log Aktivitas
        </h1>
        <p className="text-slate-600 mt-1">Riwayat semua aktivitas sistem</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ClipboardList className="text-teal-600" size={24} />
              <div>
                <p className="text-sm text-slate-600">Total Aktivitas</p>
                <p className="text-2xl font-bold">{logs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <User className="text-blue-600" size={24} />
              <div>
                <p className="text-sm text-slate-600">User Aktif</p>
                <p className="text-2xl font-bold">
                  {new Set(logs.map(log => log.user_id)).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="text-purple-600" size={24} />
              <div>
                <p className="text-sm text-slate-600">Hari Ini</p>
                <p className="text-2xl font-bold">
                  {logs.filter(log => {
                    const logDate = new Date(log.timestamp).toDateString();
                    const today = new Date().toDateString();
                    return logDate === today;
                  }).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Riwayat Aktivitas</CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="py-12 text-center">
              <ClipboardList size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">Belum ada aktivitas.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Waktu</TableHead>
                    <TableHead className="w-[120px]">User</TableHead>
                    <TableHead className="w-[80px]">Aksi</TableHead>
                    <TableHead className="w-[100px]">Entitas</TableHead>
                    <TableHead className="w-[150px]">Nama</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-slate-600">
                        {formatDate(log.timestamp)}
                      </TableCell>
                      <TableCell className="font-medium">{log.user_name}</TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell>{getEntityBadge(log.entity_type)}</TableCell>
                      <TableCell className="font-medium">{log.entity_name}</TableCell>
                      <TableCell className="text-slate-600">
                        {log.details || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogs;
